import type { EndpointMeta } from "@/apis/types";
import {
  createFetchAndHook,
  type FetchFactory,
  type HookFactory,
} from "@/shared/factories";
import { datesHelper } from "@/shared/utils";
import { wsfScheduleApiMeta } from "../apiMeta";
import {
  type RoutesHavingServiceDisruptionsByTripDateInput,
  routesHavingServiceDisruptionsByTripDateInputSchema,
} from "./shared/serviceDisruptions.input";
import {
  type ServiceDisruption,
  serviceDisruptionSchema,
} from "./shared/serviceDisruptions.output";

/**
 * Metadata for the fetchRoutesHavingServiceDisruptionsByTripDate endpoint
 */
export const routesHavingServiceDisruptionsByTripDateMeta = {
  functionName: "fetchRoutesHavingServiceDisruptionsByTripDate",
  endpoint: "/routeshavingservicedisruptions/{TripDate}",
  inputSchema: routesHavingServiceDisruptionsByTripDateInputSchema,
  outputSchema: serviceDisruptionSchema.array(),
  sampleParams: { TripDate: datesHelper.tomorrow() },
  endpointDescription:
    "List routes with service disruptions for a specific trip date.",
} satisfies EndpointMeta<
  RoutesHavingServiceDisruptionsByTripDateInput,
  ServiceDisruption[]
>;

/**
 * Fetch function for retrieving routes with service disruptions for a specific trip date
 */
const { fetch, useHook } = createFetchAndHook<
  RoutesHavingServiceDisruptionsByTripDateInput,
  ServiceDisruption[]
>({
  api: wsfScheduleApiMeta,
  endpoint: routesHavingServiceDisruptionsByTripDateMeta,
  getCacheStrategy: () =>
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    require("./shared/serviceDisruptions.endpoints").serviceDisruptionsGroup
      .cacheStrategy,
});

/**
 * Fetch function for retrieving routes with service disruptions for a specific trip date
 */
export const fetchRoutesHavingServiceDisruptionsByTripDate: FetchFactory<
  RoutesHavingServiceDisruptionsByTripDateInput,
  ServiceDisruption[]
> = fetch;

/**
 * React Query hook for retrieving routes with service disruptions for a specific trip date
 */
export const useRoutesHavingServiceDisruptionsByTripDate: HookFactory<
  RoutesHavingServiceDisruptionsByTripDateInput,
  ServiceDisruption[]
> = useHook;
